import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, BookOpen, Calculator, GitFork, Sigma } from 'lucide-react';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutModal: React.FC<AboutModalProps> = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="pointer-events-none fixed inset-0 z-[51] flex items-center justify-center p-4"
          >
            <div className="pointer-events-auto flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-900 shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900/50 px-8 py-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
                    <BookOpen className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      About Shifting Arithmetic
                    </h2>
                    <p className="font-mono text-xs tracking-widest text-zinc-500 uppercase">
                      Mathematical Foundation
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-full p-2 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="custom-scrollbar flex-1 space-y-8 overflow-y-auto p-8">
                <section className="space-y-3">
                  <h3 className="flex items-center gap-2 text-sm font-bold tracking-wider text-emerald-400 uppercase">
                    <GitFork className="h-4 w-4" />
                    The Shifting Algorithm
                  </h3>
                  <p className="leading-relaxed text-zinc-400">
                    Shifting Arithmetic is a novel multiplication strategy that
                    utilizes triangular arrays and symmetric shifts. Unlike
                    standard multiplication, it visualizes the growth of a root
                    value as it moves through successive levels (powers) of a
                    base.
                  </p>
                </section>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2 rounded-2xl border border-zinc-800 bg-zinc-800/30 p-4">
                    <div className="flex items-center gap-2 text-sm font-bold text-white">
                      <Calculator className="h-4 w-4 text-blue-400" />
                      Parameters
                    </div>
                    <ul className="space-y-1 font-mono text-xs text-zinc-500">
                      <li>
                        <span className="text-zinc-300">Root (a):</span> The
                        starting value.
                      </li>
                      <li>
                        <span className="text-zinc-300">Base (b):</span> Growth
                        factor per level.
                      </li>
                      <li>
                        <span className="text-zinc-300">Power (c):</span> Number
                        of growth iterations.
                      </li>
                      <li>
                        <span className="text-zinc-300">Weight (w):</span>{' '}
                        Boundary shift intensity.
                      </li>
                    </ul>
                  </div>

                  <div className="space-y-2 rounded-2xl border border-zinc-800 bg-zinc-800/30 p-4">
                    <div className="flex items-center gap-2 text-sm font-bold text-white">
                      <Sigma className="h-4 w-4 text-emerald-400" />
                      Prop. 9 Identity
                    </div>
                    <p className="text-xs leading-relaxed text-zinc-500 italic">
                      Verifies that the sum of all entries across levels, plus a
                      corrective term, converges to a target power value.
                    </p>
                  </div>
                </div>

                <section className="space-y-4">
                  <h3 className="text-sm font-bold tracking-wider text-blue-400 uppercase">
                    Methodologies
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <h4 className="text-sm font-medium text-white">
                        1. Contracted Method
                      </h4>
                      <p className="text-xs leading-relaxed text-zinc-500">
                        A Pascal-like approach where adjacent elements are
                        summed to form the next level. Boundary shifts are
                        applied based on the weight, creating asymmetric growth
                        patterns.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-medium text-white">
                        2. Full Expansion
                      </h4>
                      <p className="text-xs leading-relaxed text-zinc-500">
                        Each element in a row splits into 'base' new elements.
                        The total sum is preserved, but the distribution spreads
                        symmetrically from the center.
                      </p>
                    </div>
                  </div>
                </section>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-zinc-800 bg-zinc-950/50 px-8 py-4">
                <span className="font-mono text-[10px] text-zinc-600 uppercase">
                  © 2024 Akandie Software
                </span>
                <button
                  onClick={onClose}
                  className="rounded-lg bg-zinc-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-700"
                >
                  Got it
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AboutModal;
